/**
 * Implémentations réelles des actions de callMachine.
 *
 * La machine déclare les actions comme stubs (cf. callMachine.ts). Cette
 * factory produit le bag d'overrides XState à passer via machine.provide().
 * Permet de garder la machine pure (testable / sans dépendance React)
 * tout en injectant les vraies side effects.
 *
 * Les ports sont injectés explicitement — pas de globals.
 */

import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18n from 'i18next';

export interface CallActionPorts {
  queryClient: QueryClient;
  playDialBeep: () => void;
  playConnectionBeep: () => void;
  /** Hangup Twilio SDK call par callId. */
  hangupTwilio: (callId: string) => Promise<void>;
  /** Fetch + enrichit le contact ; push ENRICH_CONTACT sur l'actor. */
  fetchEnrichedContact: (callId: string) => Promise<void>;
  /** Sauve outcome + notes côté backend. */
  saveQualification: (callId: string, outcome: string, notes: string) => Promise<void>;
  /**
   * Hangup voicemail : appele uniquement quand voicemailMode === 'off' au runtime.
   * Hangup Twilio + POST call_status=completed, call_outcome=voicemail. La
   * decision (hangup vs laisser le message s'enregistrer) est portee par
   * cette fonction, pas par la machine.
   */
  hangupVoicemail: (callId: string) => Promise<void>;
}

type ActionArgs = { context: { callId: string; outcome: string | null; notes: string } };

export function createCallActions(ports: CallActionPorts) {
  return {
    playDialBeep: () => ports.playDialBeep(),
    playConnectionBeep: () => ports.playConnectionBeep(),

    openCallSheet: () => {
      // Dérivé : DialerContext lit l'état machine, pas de setState ici.
    },
    closeCallSheet: () => {
      // Idem.
    },

    fetchEnrichedContact: ({ context }: ActionArgs) => {
      ports.fetchEnrichedContact(context.callId).catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        console.warn('[callMachine] fetchEnrichedContact failed', msg);
      });
    },

    toastVoicemail: () => {
      toast.info(i18n.t('dialer:toasts.voicemailDetected'));
    },

    invalidateCallsQuery: () => {
      ports.queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
    },

    fadeButtonToValidate: () => {
      // La transition CSS est portée par CallSheet via l'état machine (qualified).
    },

    saveQualification: ({ context }: ActionArgs) => {
      if (context.outcome) {
        ports.saveQualification(context.callId, context.outcome, context.notes).catch((e: unknown) => {
          const msg = e instanceof Error ? e.message : String(e);
          console.warn('[callMachine] saveQualification failed', msg);
          toast.error(i18n.t('dialer:toasts.qualificationSaveFailed'));
        });
      }
    },

    hangupTwilio: ({ context }: ActionArgs) => {
      ports.hangupTwilio(context.callId).catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        console.warn('[callMachine] hangupTwilio failed', msg);
      });
    },

    hangupVoicemail: ({ context }: ActionArgs) => {
      ports.hangupVoicemail(context.callId).catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        console.warn('[callMachine] hangupVoicemail failed', msg);
      });
    },

    logTransition: ({ context }: ActionArgs) => {
      console.log('[callMachine] transition', { callId: context.callId });
    },
  };
}
